import { Fragment, useEffect, useMemo, useState } from 'react';
import { Lock } from 'lucide-react';
import {
  LAND_TRANSFER_TAX_FALLBACK_PCT,
  LAND_TRANSFER_TAX_RATES,
  PROPERTY_STATES,
  formatAmountInput,
  formatPercent,
  parseAmountInput,
  propertyStateLabels,
  type PropertyState,
} from '@brimo/shared';
import { Banner, Button, Dialog, Icon, Segmented, Select, TextField, Textarea } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { usePermissions } from '../../../lib/session';
import { useKnownUsers } from '../../contacts/users';
import { updateProperty } from '../api';
import { hasManagedFields, isManagedField, managedHint, withoutManagedFields } from '../availability';
import { formatNumber, parseDecimal, parseInteger } from '../format';
import { DeveloperPicker } from '../wizard/DeveloperPicker';
import type { PatchPropertyBody, PropertyRecord, PropertyType } from '../types';
import { DOCUMENTS_URL_MESSAGE, documentsUrlValue } from './documentsFolder';
import { DESCRIPTION_FIELDS } from './description';
import styles from './dialogs.module.css';

export interface PropertyEditDialogProps {
  open: boolean;
  record: PropertyRecord;
  onClose: () => void;
  onSaved: (record: PropertyRecord) => void;
}

interface Draft {
  name: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  /** '' = automatisch nach PLZ */
  state: PropertyState | '';
  landTransferTaxPct: string;
  propertyType: PropertyType;
  developer: { id: string; name: string; initials: string } | null;
  managerUserId: string;
  unitsEstimated: string;
  yearBuiltFrom: string;
  yearBuiltUntil: string;
  constructionPhases: string;
  livingAreaTotalSqm: string;
  energyCertificate: string;
  heating: string;
  parkingSpacesCount: string;
  parkingSpacePriceCents: string;
  readyForOccupancy: string;
  notary: string;
  declarationOfDivisionOn: string;
  serviceChargePerSqmCents: string;
  commissionRatePct: string;
  capPerDealCents: string;
  yearRenovated: string;
  coOwnershipSharesTotal: string;
  locationDescription: string;
  surroundingsDescription: string;
  objectDescription: string;
  specialFeatures: string;
  additionalInformation: string;
  documentsUrl: string;
}

function initialsOf(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join('');
}

function draftOf(record: PropertyRecord): Draft {
  const f = record.fields;
  return {
    name: record.name,
    street: record.street ?? '',
    houseNumber: record.houseNumber ?? '',
    postalCode: record.postalCode ?? '',
    city: record.city ?? '',
    state: f.stateSource === 'manual' && f.state !== null ? f.state : '',
    landTransferTaxPct: f.landTransferTaxPct === null ? '' : formatNumber(f.landTransferTaxPct, 2),
    propertyType: record.propertyType,
    developer: record.developer
      ? { id: record.developer.id, name: record.developer.name, initials: initialsOf(record.developer.name) }
      : null,
    managerUserId: f.managerUserId ?? '',
    unitsEstimated: f.unitsEstimated === null ? '' : String(f.unitsEstimated),
    yearBuiltFrom: f.yearBuiltFrom === null ? '' : String(f.yearBuiltFrom),
    yearBuiltUntil: f.yearBuiltUntil === null ? '' : String(f.yearBuiltUntil),
    constructionPhases: f.constructionPhases ?? '',
    livingAreaTotalSqm: f.livingAreaTotalSqm === null ? '' : formatNumber(f.livingAreaTotalSqm, 1),
    energyCertificate: f.energyCertificate ?? '',
    heating: f.heating ?? '',
    parkingSpacesCount: f.parkingSpacesCount === null ? '' : String(f.parkingSpacesCount),
    parkingSpacePriceCents:
      f.parkingSpacePriceCents === null ? '' : formatAmountInput(f.parkingSpacePriceCents),
    readyForOccupancy: f.readyForOccupancy ?? '',
    notary: f.notary ?? '',
    declarationOfDivisionOn: f.declarationOfDivisionOn ?? '',
    serviceChargePerSqmCents:
      f.serviceChargePerSqmCents === null ? '' : formatAmountInput(f.serviceChargePerSqmCents),
    commissionRatePct: f.commissionRatePct === null ? '' : formatNumber(f.commissionRatePct, 2),
    capPerDealCents: f.capPerDealCents === null ? '' : formatAmountInput(f.capPerDealCents),
    yearRenovated: f.yearRenovated === null ? '' : String(f.yearRenovated),
    coOwnershipSharesTotal:
      f.coOwnershipSharesTotal === null ? '' : formatNumber(f.coOwnershipSharesTotal, 4),
    locationDescription: f.locationDescription ?? '',
    surroundingsDescription: f.surroundingsDescription ?? '',
    objectDescription: f.objectDescription ?? '',
    specialFeatures: f.specialFeatures ?? '',
    additionalInformation: f.additionalInformation ?? '',
    documentsUrl: f.documentsUrl ?? '',
  };
}

const text = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** Options of „Bundesland": every state with its Grunderwerbsteuer — the read-only rate table of the dialog. */
export const STATE_OPTIONS = PROPERTY_STATES.map((state) => ({
  value: state,
  label: `${propertyStateLabels[state]} · ${formatPercent(LAND_TRANSFER_TAX_RATES[state].pct, 1)}`,
}));

/** Rate „automatisch" would use for the chosen (or current) state; the fallback when none is known. */
export function automaticRate(record: PropertyRecord, state: PropertyState | ''): number {
  const effective =
    state !== '' ? state : record.fields.stateSource === 'manual' ? null : record.fields.state;
  return effective === null ? LAND_TRANSFER_TAX_FALLBACK_PCT : LAND_TRANSFER_TAX_RATES[effective].pct;
}

export function stateHint(record: PropertyRecord): string {
  const { state, stateSource } = record.fields;
  if (stateSource === 'postal_code' && state !== null) return `Nach PLZ: ${propertyStateLabels[state]}`;
  if (stateSource === 'manual') return 'Manuell gewählt. „Automatisch nach PLZ" leitet es neu ab.';
  return 'Bundesland unbekannt — bitte PLZ prüfen oder Bundesland wählen.';
}

/** Objekte 03 „Objekt bearbeiten" / „Bearbeiten": the basics and the 12 Objektdaten fields → `PATCH /properties/:id`. */
export function PropertyEditDialog({ open, record, onClose, onSaved }: PropertyEditDialogProps) {
  const permissions = usePermissions();
  const canSetRate = permissions.is('properties.release', 'yes');
  const [draft, setDraft] = useState<Draft>(() => draftOf(record));
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const users = useKnownUsers(useMemo(() => (record.manager ? [record.manager] : []), [record.manager]));
  const external = record.external;
  const locked = (field: string): boolean => isManagedField(external, field);
  /** Props of a field TeamProQ owns while the connection is import-only (read-only, lock, hint). */
  const lock = (field: string, hint?: string) =>
    locked(field) && external
      ? {
          readOnly: true,
          hint: managedHint(external),
          labelEnd: <Icon icon={Lock} size={16} aria-label="Gesperrt" />,
        }
      : { hint };

  useEffect(() => {
    if (open) {
      setDraft(draftOf(record));
      setFields({});
      setError(null);
    }
  }, [open, record]);

  function set<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  async function submit() {
    const errors: Record<string, string> = {};
    if (!draft.name.trim()) errors['name'] = 'Bitte einen Arbeitstitel angeben.';
    if (!draft.developer) errors['developerId'] = 'Bitte wählen Sie einen Bauträger.';
    const numeric = (value: string, key: string, parse: (t: string) => number | null): number | null => {
      if (value.trim() === '') return null;
      const parsed = parse(value);
      if (parsed === null && !locked(key)) errors[key] = 'Bitte eine Zahl angeben.';
      return parsed;
    };
    const body: PatchPropertyBody = {
      name: draft.name.trim(),
      street: text(draft.street),
      houseNumber: text(draft.houseNumber),
      postalCode: text(draft.postalCode),
      city: text(draft.city),
      landTransferTaxPct: numeric(draft.landTransferTaxPct, 'landTransferTaxPct', parseDecimal),
      propertyType: draft.propertyType,
      managerUserId: draft.managerUserId || null,
      unitsEstimated: numeric(draft.unitsEstimated, 'unitsEstimated', parseInteger),
      yearBuiltFrom: numeric(draft.yearBuiltFrom, 'yearBuiltFrom', parseInteger),
      yearBuiltUntil: numeric(draft.yearBuiltUntil, 'yearBuiltUntil', parseInteger),
      constructionPhases: text(draft.constructionPhases),
      livingAreaTotalSqm: numeric(draft.livingAreaTotalSqm, 'livingAreaTotalSqm', parseDecimal),
      energyCertificate: text(draft.energyCertificate),
      heating: text(draft.heating),
      parkingSpacesCount: numeric(draft.parkingSpacesCount, 'parkingSpacesCount', parseInteger),
      parkingSpacePriceCents: numeric(
        draft.parkingSpacePriceCents,
        'parkingSpacePriceCents',
        parseAmountInput,
      ),
      readyForOccupancy: text(draft.readyForOccupancy),
      notary: text(draft.notary),
      declarationOfDivisionOn: text(draft.declarationOfDivisionOn),
      serviceChargePerSqmCents: numeric(
        draft.serviceChargePerSqmCents,
        'serviceChargePerSqmCents',
        parseAmountInput,
      ),
      yearRenovated: numeric(draft.yearRenovated, 'yearRenovated', parseInteger),
      coOwnershipSharesTotal: numeric(draft.coOwnershipSharesTotal, 'coOwnershipSharesTotal', parseDecimal),
      locationDescription: text(draft.locationDescription),
      surroundingsDescription: text(draft.surroundingsDescription),
      objectDescription: text(draft.objectDescription),
      specialFeatures: text(draft.specialFeatures),
      additionalInformation: text(draft.additionalInformation),
    };
    const documentsUrl = documentsUrlValue(draft.documentsUrl);
    if (!documentsUrl.valid) errors['documentsUrl'] = DOCUMENTS_URL_MESSAGE;
    body.documentsUrl = documentsUrl.url;
    if (draft.developer) body.developerId = draft.developer.id;
    if (draft.state !== draftOf(record).state) body.state = draft.state === '' ? null : draft.state;
    if (canSetRate) {
      body.commissionRatePct = numeric(draft.commissionRatePct, 'commissionRatePct', parseDecimal);
      body.capPerDealCents = numeric(draft.capPerDealCents, 'capPerDealCents', parseAmountInput);
    }
    if (Object.keys(errors).length > 0) {
      setFields(errors);
      return;
    }
    setFields({});
    setError(null);
    setPending(true);
    try {
      onSaved(await updateProperty(record.id, withoutManagedFields(body, external)));
    } catch (err) {
      if (err instanceof ApiError && Object.keys(err.fields).length > 0) setFields(err.fields);
      else setError(errorMessage(err));
    } finally {
      setPending(false);
    }
  }

  /**
   * Sections in their screen order, the ones with fields TeamProQ owns last (Nico feedback FB-004, D-315): the
   * editable fields come first; within „Objektdaten" the locked year/MEA fields move behind the editable ones.
   */
  const managedDataLocked = [
    'yearBuiltFrom',
    'yearBuiltUntil',
    'yearRenovated',
    'coOwnershipSharesTotal',
  ].some(locked);
  const basicsNode = (
    <div className={styles.group}>
      <div className={styles.groupTitle}>Grunddaten</div>
      <TextField
        label="Arbeitstitel"
        required
        value={draft.name}
        error={fields['name']}
        {...lock('name')}
        onChange={(event) => set('name', event.target.value)}
      />
      <div className={styles.grid3}>
        <TextField
          label="Straße"
          className={styles.span2}
          value={draft.street}
          {...lock('street')}
          onChange={(event) => set('street', event.target.value)}
        />
        <TextField
          label="Hausnummer"
          value={draft.houseNumber}
          {...lock('houseNumber')}
          onChange={(event) => set('houseNumber', event.target.value)}
        />
        <TextField
          label="PLZ"
          value={draft.postalCode}
          {...lock('postalCode')}
          onChange={(event) => set('postalCode', event.target.value)}
        />
        <TextField
          label="Ort"
          className={styles.span2}
          value={draft.city}
          {...lock('city')}
          onChange={(event) => set('city', event.target.value)}
        />
      </div>
      <div className={styles.grid2}>
        <Select
          label="Bundesland"
          value={draft.state}
          placeholder="Automatisch nach PLZ"
          options={STATE_OPTIONS}
          error={fields['state']}
          hint={stateHint(record)}
          onChange={(event) => set('state', event.target.value as PropertyState | '')}
        />
        <TextField
          label="Grunderwerbsteuer"
          inputMode="decimal"
          optional
          trailing={<span className={styles.suffix}>%</span>}
          value={draft.landTransferTaxPct}
          placeholder={formatNumber(automaticRate(record, draft.state), 2)}
          error={fields['landTransferTaxPct']}
          hint="Leer = automatisch nach Bundesland."
          onChange={(event) => set('landTransferTaxPct', event.target.value)}
        />
      </div>
      <div className={styles.fieldBlock}>
        <span className={styles.fieldLabel}>Objektart</span>
        <Segmented<PropertyType>
          label="Objektart"
          value={draft.propertyType}
          items={[
            { value: 'existing', label: 'Bestand' },
            { value: 'new_build', label: 'Neubau' },
            { value: 'core_renovation', label: 'Kernsanierung' },
          ]}
          onChange={(value) => set('propertyType', value)}
        />
      </div>
      <DeveloperPicker
        value={draft.developer}
        onChange={(developer) => set('developer', developer)}
        error={fields['developerId']}
        hint=""
      />
      <div className={styles.grid2}>
        <Select
          label="Objektbetreuung"
          value={draft.managerUserId}
          placeholder="Keine Zuordnung"
          options={users.map((user) => ({ value: user.id, label: user.shortName }))}
          onChange={(event) => set('managerUserId', event.target.value)}
        />
        <TextField
          label="Einheiten (geschätzt)"
          inputMode="numeric"
          value={draft.unitsEstimated}
          error={fields['unitsEstimated']}
          onChange={(event) => set('unitsEstimated', event.target.value)}
        />
      </div>
    </div>
  );
  const yearDataFields = (
    <>
      <TextField
        label="Baujahr von"
        inputMode="numeric"
        value={draft.yearBuiltFrom}
        error={fields['yearBuiltFrom']}
        {...lock('yearBuiltFrom')}
        onChange={(event) => set('yearBuiltFrom', event.target.value)}
      />
      <TextField
        label="Baujahr bis"
        inputMode="numeric"
        value={draft.yearBuiltUntil}
        error={fields['yearBuiltUntil']}
        {...lock('yearBuiltUntil')}
        onChange={(event) => set('yearBuiltUntil', event.target.value)}
      />
      <TextField
        label="Sanierungsjahr"
        inputMode="numeric"
        value={draft.yearRenovated}
        error={fields['yearRenovated']}
        {...lock('yearRenovated')}
        onChange={(event) => set('yearRenovated', event.target.value)}
      />
      <TextField
        label="MEA gesamt"
        inputMode="decimal"
        value={draft.coOwnershipSharesTotal}
        error={fields['coOwnershipSharesTotal']}
        {...lock('coOwnershipSharesTotal', 'Summe der Miteigentumsanteile laut Teilungserklärung')}
        onChange={(event) => set('coOwnershipSharesTotal', event.target.value)}
      />
    </>
  );
  const otherDataFields = (
    <>
      <TextField
        label="Bauabschnitte"
        value={draft.constructionPhases}
        hint="z. B. „2 (Haus A, Haus B)“"
        onChange={(event) => set('constructionPhases', event.target.value)}
      />
      <TextField
        label="Wohnfläche gesamt"
        inputMode="decimal"
        trailing={<span className={styles.suffix}>m²</span>}
        value={draft.livingAreaTotalSqm}
        error={fields['livingAreaTotalSqm']}
        onChange={(event) => set('livingAreaTotalSqm', event.target.value)}
      />
      <TextField
        label="Energieausweis"
        value={draft.energyCertificate}
        onChange={(event) => set('energyCertificate', event.target.value)}
      />
      <TextField
        label="Heizung"
        value={draft.heating}
        onChange={(event) => set('heating', event.target.value)}
      />
      <TextField
        label="Stellplätze (Anzahl)"
        inputMode="numeric"
        value={draft.parkingSpacesCount}
        error={fields['parkingSpacesCount']}
        onChange={(event) => set('parkingSpacesCount', event.target.value)}
      />
      <TextField
        label="Preis je Stellplatz"
        type="money"
        value={draft.parkingSpacePriceCents}
        error={fields['parkingSpacePriceCents']}
        onChange={(event) => set('parkingSpacePriceCents', event.target.value)}
      />
      <TextField
        label="Bezugsfertig"
        value={draft.readyForOccupancy}
        onChange={(event) => set('readyForOccupancy', event.target.value)}
      />
      <TextField label="Notar" value={draft.notary} onChange={(event) => set('notary', event.target.value)} />
      <TextField
        label="Teilungserklärung vom"
        type="date"
        value={draft.declarationOfDivisionOn}
        error={fields['declarationOfDivisionOn']}
        onChange={(event) => set('declarationOfDivisionOn', event.target.value)}
      />
      <TextField
        label="Hausgeld Ø je m² mtl."
        type="money"
        value={draft.serviceChargePerSqmCents}
        error={fields['serviceChargePerSqmCents']}
        onChange={(event) => set('serviceChargePerSqmCents', event.target.value)}
      />
      <TextField
        className={styles.span2}
        label="Externer Unterlagenordner"
        type="url"
        optional
        placeholder="https://"
        value={draft.documentsUrl}
        error={fields['documentsUrl']}
        onChange={(event) => set('documentsUrl', event.target.value)}
      />
      {canSetRate ? (
        <>
          <TextField
            label="Provision"
            inputMode="decimal"
            trailing={<span className={styles.suffix}>%</span>}
            value={draft.commissionRatePct}
            error={fields['commissionRatePct']}
            hint="Gilt für neue Reservierungen, nicht rückwirkend."
            onChange={(event) => set('commissionRatePct', event.target.value)}
          />
          <TextField
            label="Kappung je Deal"
            type="money"
            optional
            value={draft.capPerDealCents}
            error={fields['capPerDealCents']}
            onChange={(event) => set('capPerDealCents', event.target.value)}
          />
        </>
      ) : null}
    </>
  );
  const dataNode = (
    <div className={styles.group}>
      <div className={styles.groupTitle}>Objektdaten</div>
      <div className={styles.grid2}>
        {managedDataLocked ? (
          <>
            {otherDataFields}
            {yearDataFields}
          </>
        ) : (
          <>
            {yearDataFields}
            {otherDataFields}
          </>
        )}
      </div>
    </div>
  );
  const descriptionNode = (
    <div className={styles.group}>
      <div className={styles.groupTitle}>Beschreibung</div>
      {DESCRIPTION_FIELDS.map(({ key, label }) => (
        <Textarea
          key={key}
          label={label}
          optional
          autoGrow
          rows={3}
          value={draft[key]}
          {...lock(key)}
          onChange={(event) => set(key, event.target.value)}
        />
      ))}
    </div>
  );
  const sections = [
    {
      key: 'basics',
      locked: ['name', 'street', 'houseNumber', 'postalCode', 'city'].some(locked),
      node: basicsNode,
    },
    { key: 'data', locked: managedDataLocked, node: dataNode },
    { key: 'description', locked: DESCRIPTION_FIELDS.some(({ key }) => locked(key)), node: descriptionNode },
  ].sort((a, b) => Number(a.locked) - Number(b.locked));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      eyebrow="Objektakte"
      title="Objekt bearbeiten"
      subtitle="Grunddaten und Objektdaten. Die Änderungen erscheinen sofort in der Akte."
      width={680}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending}>
            Abbrechen
          </Button>
          <Button loading={pending} onClick={() => void submit()}>
            Speichern
          </Button>
        </>
      }
    >
      <div className={styles.form}>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        {hasManagedFields(external) && external ? (
          <Banner tone="info">
            Felder mit Schloss werden aus {external.connectionName} übernommen und können hier nicht geändert
            werden.
          </Banner>
        ) : null}
        {sections.map((section) => (
          <Fragment key={section.key}>{section.node}</Fragment>
        ))}
      </div>
    </Dialog>
  );
}
